import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { useTheme } from '../lib/theme';
import { Button } from '../components/ui/button';
import {
  Shield, Users, Activity, TrendingUp, Search, Plus, Minus, Trash2,
  BarChart3, Clock, AlertCircle, CheckCircle, Sun, Moon, ArrowLeft
} from 'lucide-react';
import api, { adminAPI } from '../lib/api';

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const { language } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('kpis');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAdmin = (email) => {
    if (!email) return false;
    return email.toLowerCase().endsWith('@sheetarmor.io');
  };

  // Check if user is admin (client-side verification)
  // CRITICAL: Wait for auth to finish loading before checking
  useEffect(() => {
    if (authLoading) return; // Wait for auth to load

    if (!user || !isAdmin(user.email)) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  // Fetch data on mount
  useEffect(() => {
    if (authLoading) return; // Wait for auth to load

    if (user && isAdmin(user.email)) {
      fetchAllData();
    }
  }, [user, authLoading]);

  const fetchAllData = async () => {
    setDataLoading(true);
    setError(null);
    try {
      const [statsRes, usersRes, logsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users'),
        api.get('/admin/audits/logs?limit=100')
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setLogs(logsRes.data.logs);
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
      setError(err.response?.data?.detail || 'Failed to load admin data');
      if (err.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setDataLoading(false);
    }
  };

  const handleAdjustAudits = async (userId, adjustment, userEmail) => {
    const reason = prompt(`Adjust audits for ${userEmail} by ${adjustment > 0 ? '+' : ''}${adjustment}.\n\nReason (required):`);
    if (!reason || reason.trim() === '') {
      alert('Reason is required for audit adjustments.');
      return;
    }

    try {
      await api.post(`/admin/users/${userId}/adjust_audits`, {
        adjustment,
        reason: reason.trim()
      });
      alert(`Successfully adjusted audits for ${userEmail}`);
      fetchAllData(); // Refresh data
    } catch (err) {
      alert(`Failed to adjust audits: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    // Guard: if userId is somehow undefined/null, abort early with a clear message
    if (!userId) {
      alert('Cannot delete: user ID is missing. Please refresh the page and try again.');
      return;
    }

    const confirmed = window.confirm(
      `Êtes-vous sûr de vouloir supprimer définitivement cet utilisateur et toutes ses données ?\n\n${userEmail}`
    );
    if (!confirmed) return;

    try {
      await adminAPI.deleteUser(userId);
      // Optimistic update — remove the row immediately without a full refresh
      setUsers(prev => prev.filter(u => u.user_id !== userId));
    } catch (err) {
      alert(`Failed to delete user: ${err.response?.data?.detail || err.message}`);
    }
  };

  const filteredUsers = users.filter(u =>
    u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Show loading spinner while auth is loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // After auth loads, check if user is admin
  if (!user || !isAdmin(user.email)) {
    return null; // Redirect will happen via useEffect
  }

  if (dataLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#050505] flex items-center justify-center">
        <div className="text-slate-600 dark:text-neutral-400">Loading admin dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#050505] flex items-center justify-center">
        <div className="text-red-600 dark:text-red-400">
          <AlertCircle className="w-8 h-8 mx-auto mb-2" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#050505] text-slate-900 dark:text-white">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-white/10 bg-white/80 dark:bg-[#050505]/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-[90rem] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-1" strokeWidth={1.5} />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
              <h1 className="font-heading text-xl tracking-tight">Admin Dashboard</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white h-8 w-8 p-0"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" strokeWidth={1.5} /> : <Moon className="w-4 h-4" strokeWidth={1.5} />}
            </Button>
            <div className="text-sm text-slate-600 dark:text-neutral-400">
              {user.email}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[90rem] mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-8 border-b border-slate-200 dark:border-white/10">
          <button
            onClick={() => setActiveTab('kpis')}
            className={`px-4 py-2 text-sm font-body transition-colors border-b-2 ${
              activeTab === 'kpis'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white'
            }`}
          >
            <TrendingUp className="w-4 h-4 inline mr-1" strokeWidth={1.5} />
            KPIs Marketing
          </button>
          <button
            onClick={() => setActiveTab('crm')}
            className={`px-4 py-2 text-sm font-body transition-colors border-b-2 ${
              activeTab === 'crm'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white'
            }`}
          >
            <Users className="w-4 h-4 inline mr-1" strokeWidth={1.5} />
            CRM / Clients
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`px-4 py-2 text-sm font-body transition-colors border-b-2 ${
              activeTab === 'logs'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white'
            }`}
          >
            <Activity className="w-4 h-4 inline mr-1" strokeWidth={1.5} />
            Audit Logs
          </button>
        </div>

        {/* KPIs Tab */}
        {activeTab === 'kpis' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Total Users */}
              <div className="bg-slate-50 dark:bg-[#0A0A0A] border border-slate-200 dark:border-white/10 rounded-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <Users className="w-8 h-8 text-blue-600" strokeWidth={1.5} />
                </div>
                <p className="text-3xl font-heading text-slate-900 dark:text-white mb-1">
                  {stats.total_users.toLocaleString()}
                </p>
                <p className="text-sm text-slate-600 dark:text-neutral-400">Total Users</p>
              </div>

              {/* Total Audits */}
              <div className="bg-slate-50 dark:bg-[#0A0A0A] border border-slate-200 dark:border-white/10 rounded-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <BarChart3 className="w-8 h-8 text-green-600" strokeWidth={1.5} />
                </div>
                <p className="text-3xl font-heading text-slate-900 dark:text-white mb-1">
                  {stats.total_audits.toLocaleString()}
                </p>
                <p className="text-sm text-slate-600 dark:text-neutral-400">Total Audits</p>
              </div>

              {/* Total Issues Detected */}
              <div className="bg-slate-50 dark:bg-[#0A0A0A] border border-slate-200 dark:border-white/10 rounded-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" strokeWidth={1.5} />
                </div>
                <p className="text-3xl font-heading text-slate-900 dark:text-white mb-1">
                  {stats.total_issues_detected.toLocaleString()}
                </p>
                <p className="text-sm text-slate-600 dark:text-neutral-400">Errors Detected</p>
              </div>

              {/* Plan Distribution */}
              <div className="bg-slate-50 dark:bg-[#0A0A0A] border border-slate-200 dark:border-white/10 rounded-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <Shield className="w-8 h-8 text-purple-600" strokeWidth={1.5} />
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    Free: <span className="text-slate-900 dark:text-white font-heading">{stats.plan_distribution.free || 0}</span>
                  </p>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    Auditor: <span className="text-slate-900 dark:text-white font-heading">{stats.plan_distribution.auditor || 0}</span>
                  </p>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    Firm: <span className="text-slate-900 dark:text-white font-heading">{stats.plan_distribution.firm || 0}</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Marketing Copy */}
            <div className="bg-gradient-to-r from-blue-600/10 to-purple-600/10 border border-blue-600/30 rounded-sm p-8">
              <h2 className="font-heading text-2xl text-slate-900 dark:text-white mb-4">
                Marketing Copy (English)
              </h2>
              <p className="text-slate-700 dark:text-neutral-300 text-lg leading-relaxed mb-4">
                "SheetArmor has analyzed <strong className="text-blue-600">{stats.total_audits.toLocaleString()}</strong> financial models and detected over <strong className="text-red-600">{stats.total_issues_detected.toLocaleString()}</strong> critical errors that could have cost businesses millions. Join <strong className="text-purple-600">{stats.total_users.toLocaleString()}</strong> finance professionals who trust us to secure their spreadsheets."
              </p>
              <h2 className="font-heading text-2xl text-slate-900 dark:text-white mb-4 mt-8">
                Marketing Copy (Français)
              </h2>
              <p className="text-slate-700 dark:text-neutral-300 text-lg leading-relaxed">
                "SheetArmor a analysé <strong className="text-blue-600">{stats.total_audits.toLocaleString()}</strong> modèles financiers et détecté plus de <strong className="text-red-600">{stats.total_issues_detected.toLocaleString()}</strong> erreurs critiques qui auraient pu coûter des millions aux entreprises. Rejoignez <strong className="text-purple-600">{stats.total_users.toLocaleString()}</strong> professionnels de la finance qui nous font confiance pour sécuriser leurs tableurs."
              </p>
            </div>
          </div>
        )}

        {/* CRM Tab */}
        {activeTab === 'crm' && (
          <div className="space-y-6">
            {/* Search Bar */}
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
                <input
                  type="text"
                  placeholder="Search by email or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-[#0A0A0A] border border-slate-200 dark:border-white/10 rounded-sm text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-neutral-600 focus:outline-none focus:ring-2 focus:ring-blue-600/50"
                />
              </div>
              <div className="text-sm text-slate-600 dark:text-neutral-400">
                {filteredUsers.length} / {users.length} users
              </div>
            </div>

            {/* Users Table */}
            <div className="bg-slate-50 dark:bg-[#0A0A0A] border border-slate-200 dark:border-white/10 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-white/10">
                      <th className="px-4 py-3 text-left text-xs font-body text-slate-600 dark:text-neutral-400 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-body text-slate-600 dark:text-neutral-400 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-body text-slate-600 dark:text-neutral-400 uppercase tracking-wider">Plan</th>
                      <th className="px-4 py-3 text-left text-xs font-body text-slate-600 dark:text-neutral-400 uppercase tracking-wider">Used</th>
                      <th className="px-4 py-3 text-left text-xs font-body text-slate-600 dark:text-neutral-400 uppercase tracking-wider">Remaining</th>
                      <th className="px-4 py-3 text-left text-xs font-body text-slate-600 dark:text-neutral-400 uppercase tracking-wider">Created</th>
                      <th className="px-4 py-3 text-left text-xs font-body text-slate-600 dark:text-neutral-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.user_id} className="border-b border-slate-200 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-[#111] transition-colors">
                        <td className="px-4 py-3 text-sm text-slate-900 dark:text-white font-mono">{u.email}</td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-neutral-400">{u.name}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-1 text-xs font-body rounded-sm ${
                            u.plan === 'firm' ? 'bg-purple-600/20 text-purple-600' :
                            u.plan === 'auditor' ? 'bg-blue-600/20 text-blue-600' :
                            'bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-neutral-400'
                          }`}>
                            {u.plan.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">{u.audits_used}</td>
                        <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">
                          {typeof u.audits_remaining === 'string' ? u.audits_remaining : u.audits_remaining}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-neutral-400">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleAdjustAudits(u.user_id, 1, u.email)}
                              className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-600/10"
                              title="Add +1 audit"
                            >
                              <Plus className="w-3.5 h-3.5" strokeWidth={2} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleAdjustAudits(u.user_id, -1, u.email)}
                              className="h-7 w-7 p-0 text-orange-600 hover:text-orange-700 hover:bg-orange-600/10"
                              title="Remove -1 audit"
                            >
                              <Minus className="w-3.5 h-3.5" strokeWidth={2} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(u.user_id, u.email)}
                              disabled={!u.user_id}
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-400 hover:bg-red-600/10 disabled:opacity-30 disabled:cursor-not-allowed"
                              title={u.user_id ? "Delete user permanently" : "User ID missing — cannot delete"}
                            >
                              <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl text-slate-900 dark:text-white">
                Recent Activity
              </h2>
              <div className="text-sm text-slate-600 dark:text-neutral-400">
                {logs.length} audits
              </div>
            </div>

            {/* Logs List */}
            <div className="space-y-3">
              {logs.map((log) => (
                <div
                  key={log.audit_id}
                  className="bg-slate-50 dark:bg-[#0A0A0A] border border-slate-200 dark:border-white/10 rounded-sm p-4 hover:border-blue-600/30 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${
                        log.risk_level === 'critical' ? 'bg-red-600/20' :
                        log.risk_level === 'high' ? 'bg-orange-600/20' :
                        log.risk_level === 'medium' ? 'bg-yellow-600/20' :
                        'bg-green-600/20'
                      }`}>
                        {log.risk_level === 'critical' || log.risk_level === 'high' ? (
                          <AlertCircle className="w-4 h-4 text-red-600" strokeWidth={2} />
                        ) : (
                          <CheckCircle className="w-4 h-4 text-green-600" strokeWidth={2} />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-sm text-slate-900 dark:text-white">
                            {log.user_email}
                          </span>
                          <span className="text-xs text-slate-400 dark:text-neutral-600">•</span>
                          <span className="text-xs text-slate-600 dark:text-neutral-400">
                            {new Date(log.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-slate-600 dark:text-neutral-400">
                            Health Score: <span className="text-slate-900 dark:text-white font-heading">{log.health_score}%</span>
                          </span>
                          <span className="text-slate-400 dark:text-neutral-600">•</span>
                          <span className="text-slate-600 dark:text-neutral-400">
                            Issues: <span className="text-slate-900 dark:text-white font-heading">{log.issue_count}</span>
                          </span>
                          <span className="text-slate-400 dark:text-neutral-600">•</span>
                          <span className={`text-xs px-2 py-0.5 rounded-sm ${
                            log.risk_level === 'critical' ? 'bg-red-600/20 text-red-600' :
                            log.risk_level === 'high' ? 'bg-orange-600/20 text-orange-600' :
                            log.risk_level === 'medium' ? 'bg-yellow-600/20 text-yellow-600' :
                            'bg-green-600/20 text-green-600'
                          }`}>
                            {log.risk_level?.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-slate-400 dark:text-neutral-600 font-mono">
                      {log.audit_id.slice(0, 8)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
